// src/hooks/useOrgSettings.js
import { useState, useEffect } from "react";
import { doc, onSnapshot, updateDoc, collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";

function parseGuidelineItems(text) {
  return (text || "").trim().split("\n")
    .map((line) => line.replace(/^\d+\.\s*/, "").trim())
    .filter(Boolean);
}

export const DEFAULT_GUIDELINES = `1. No naming names — describe the situation, not the individual.
2. Be specific — include shift, time, gate, or location when relevant.
3. One issue per post — submit multiple pinch points separately.
4. Choose severity honestly — Critical = safety or major service impact.
5. Keep it constructive — this is not a complaint box; inform and prevent.`;

export const DEFAULT_CATEGORIES = [
  "Staffing", "Communication", "Equipment", "Safety",
  "Customer", "Other",
];

export function useOrgSettings(orgId) {
  const [orgName, setOrgName] = useState("");
  const [guidelines, setGuidelines] = useState(DEFAULT_GUIDELINES);
  const [guidelineItems, setGuidelineItems] = useState(parseGuidelineItems(DEFAULT_GUIDELINES));
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [anonSubmitEnabled, setAnonSubmitEnabled] = useState(true);

  useEffect(() => {
    if (!orgId) return;
    const unsub = onSnapshot(doc(db, "organizations", orgId), (snap) => {
      if (!snap.exists()) return;
      const data = snap.data();
      if (data.name) setOrgName(data.name);
      if (data.guidelines) {
        setGuidelines(data.guidelines);
        setGuidelineItems(data.guidelineItems || parseGuidelineItems(data.guidelines));
      }
      if (data.categories?.length) setCategories(data.categories);
      if (typeof data.anonSubmitEnabled === "boolean") setAnonSubmitEnabled(data.anonSubmitEnabled);
    });
    return unsub;
  }, [orgId]);

  const updateGuidelines = async (items, savedBy) => {
    const text = items.map((item, i) => `${i + 1}. ${item}`).join("\n");
    await updateDoc(doc(db, "organizations", orgId), { guidelines: text, guidelineItems: items });
    await addDoc(collection(db, "organizations", orgId, "guidelineVersions"), {
      items,
      savedAt: serverTimestamp(),
      savedBy: savedBy || "unknown",
    });
  };
  const updateCategories = (cats) =>
    updateDoc(doc(db, "organizations", orgId), { categories: cats });
  const updateAnonSubmitEnabled = (val) =>
    updateDoc(doc(db, "organizations", orgId), { anonSubmitEnabled: val });

  return { orgName, guidelines, guidelineItems, categories, anonSubmitEnabled, updateGuidelines, updateCategories, updateAnonSubmitEnabled };
}
